import { useStatsFiltersStore } from "../stats-filters-store";
import { SelectedColumnsState, TQuintiles, FilterKeys, PlayerEnum } from "../types";
import type { StatsFilterRanges } from "../types";
import { getDefaultRangeForKey } from "../stats-filters-store";
import { STATS_FILTERS, PERCENTAGE_STAT_KEYS } from "./constants";

export const getPaginatorFilter = (): number => {
	const pagination = useStatsFiltersStore.getState().pagination;
	return pagination?.page ?? 0;
};

export const fetchSelectedColumnState = (): SelectedColumnsState => {
	const state = useStatsFiltersStore.getState().selectedColumnsState;
	if (state) return state;
	return { items: [], currentSelected: "" };
};

export const getSelectedSet = (): string => {
	return useStatsFiltersStore.getState().selectedColumnsId ?? "General";
};

export const getSet = (name: string): string[] | undefined => {
	return fetchSelectedColumnState()?.items.find((column: { name: string; selectedColumns?: string[] }) => column.name == name)?.selectedColumns;
};

/** True if value is in 0–1 (backend) scale; false if in 0–100 (percentile) scale. */
const inZeroOneScale = (v: number): boolean => v >= 0 && v <= 1;

const PERCENTAGE_KEYS_SET = new Set(PERCENTAGE_STAT_KEYS);

export const getRange = (key: string, from: number, to: number, quintiles: TQuintiles) => {
	if (PERCENTAGE_KEYS_SET.has(key)) {
		// Percentage sliders (0–100% UI). Support both stored scales: 0–1 (e.g. migrated) and 0–100 (form).
		const alreadyZeroOne = inZeroOneScale(from) && inZeroOneScale(to);
		const gte = alreadyZeroOne ? from : from / 100;
		const lte = alreadyZeroOne ? to : to / 100;
		return { gte, lte };
	}
	if (key === FilterKeys.GAMES) {
		if (from === 1 && to !== 100 && to) {
			return { lte: to };
		} // gte - lte

		if (to === 100 && from !== 1 && from) {
			return { gte: from };
		}
		return { gte: from, lte: to };
	}
	if (key === FilterKeys.MINUTES) {
		if (from && to !== 900) {
			return { gte: from, lte: to };
		}

		if (to === 900 && from !== 90 && from) {
			return { gte: from };
		}
		return { gte: from, lte: to };
	}
	if (key === FilterKeys.GOALS) {
		if (from === 0 && to !== 100 && to) {
			return { lte: to };
		}
		if (to === 10 && from !== 0 && from) {
			return { gte: from };
		}
		return { gte: from, lte: to };
	}
	if (key === FilterKeys.GOALS_OWN) {
		if (from === 0 && to !== 100 && to) {
			return { lte: to };
		}
		if (to === 100 && from !== 0 && from) {
			return { gte: from };
		}
		return { gte: from, lte: to };
	}
	if (key === FilterKeys.TOTAL_GOALS || key === FilterKeys.TOTAL_ASSISTS) {
		const mappedValue: { [key: number]: number | string } = { 0: 0, 20: 1, 40: 5, 60: 10, 80: 20, 100: "MAX" };
		if (from === 0 && to !== 100 && to) {
			return { lte: mappedValue[to] };
		}
		if (to === 100 && from !== 0 && from) {
			return { gte: mappedValue[from] };
		}
		return { gte: mappedValue[from], lte: mappedValue[to] };
	}
};

/** Returns true if the range is the default for this key (no need to send to backend). */
function isDefaultRange(key: string, range: [number, number]): boolean {
	const def = getDefaultRangeForKey(key);
	return range[0] === def[0] && range[1] === def[1];
}

/**
 * Converts stored range form values to backend stats filter shape (gte/lte per stat).
 * Only includes stats that differ from default; quintiles are applied at send time.
 */
export function getInitStatsFilters(ranges: StatsFilterRanges, quintiles: TQuintiles): Record<string, { gte?: number; lte?: number }> {
	const statsFilter: Record<string, { gte?: number; lte?: number }> = {};
	STATS_FILTERS.forEach((key: string) => {
		const range = ranges[key] ?? getDefaultRangeForKey(key);
		if (isDefaultRange(key, range)) return;

		const from = Math.min(range[0], range[1]);
		const to = Math.max(range[0], range[1]);
		const backendRange = getRange(key, from, to, quintiles);
		if (backendRange) {
			const normalized =
				"lt" in backendRange && !("lte" in backendRange)
					? { ...backendRange, lte: (backendRange as { lt: number }).lt, lt: undefined }
					: backendRange;
			statsFilter[key] = normalized as { gte?: number; lte?: number };
		}
	});
	return statsFilter;
}

export type Statistics = {
	statistics: {
		[key: string]: { position: string; percentile: number; value: number }[];
	};
};

// -- left filters utils --

export const getInitLeftFilters = (defaults?: { clubId?: number; teamId?: number }) => {
	const initialValue = useStatsFiltersStore.getState().leftFilters as Record<string, unknown> | null;

	if (initialValue) {
		let leftFilter: any = {};
		if (initialValue[PlayerEnum.birthday]) {
			const arr = initialValue[PlayerEnum.birthday] as [number, number];
			const range = { gte: arr[0], lte: arr[1] };
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.birthday]: range } };
		}
		if (initialValue[PlayerEnum.height]) {
			const arr = initialValue[PlayerEnum.height] as [number, number];
			const range = { gte: arr[0], lte: arr[1] };
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.height]: range } };
		}
		if (initialValue[PlayerEnum.weight]) {
			const arr = initialValue[PlayerEnum.weight] as [number, number];
			const range = { gte: arr[0], lte: arr[1] };
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.weight]: range } };
		}

		if (initialValue[PlayerEnum.clubName]) {
			leftFilter = { ...leftFilter, clubName: [(initialValue.clubName as { value?: number })?.value] };
		}

		if (initialValue[PlayerEnum.countryCodes]) {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.countryCodes]: [initialValue[PlayerEnum.countryCodes]] } };
		}

		if (initialValue[PlayerEnum.nationality] && initialValue[PlayerEnum.nationality] !== "-") {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.nationality]: [initialValue[PlayerEnum.nationality]] } };
		}

		if (initialValue[PlayerEnum.playerName]) {
			leftFilter = { ...leftFilter, playerName: (initialValue.playerName as { value?: string })?.value };
		}

		if (initialValue[PlayerEnum.position] && initialValue[PlayerEnum.position] !== "-") {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.position]: initialValue[PlayerEnum.position] } };
		}

		if (initialValue[PlayerEnum.strongFoot] && initialValue[PlayerEnum.strongFoot] !== "") {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.strongFoot]: initialValue[PlayerEnum.strongFoot] } };
		}

		if (initialValue[PlayerEnum.playedInNationalTeam]) {
			let playedInNationalTeam = null;

			if (initialValue[PlayerEnum.playedInNationalTeam] === "Yes") {
				playedInNationalTeam = 1;
			} else if (initialValue[PlayerEnum.playedInNationalTeam] === "No") {
				playedInNationalTeam = 0;
			}

			if (playedInNationalTeam) {
				leftFilter = { ...leftFilter, ...{ [PlayerEnum.playedInNationalTeam]: playedInNationalTeam } };
			}
		}

		if (initialValue[PlayerEnum.gender] && initialValue[PlayerEnum.gender] !== "") {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.gender]: initialValue[PlayerEnum.gender] } };
		}

		if (initialValue[PlayerEnum.teamAgeGroup] && initialValue[PlayerEnum.teamAgeGroup] !== "-") {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.teamAgeGroup]: [initialValue[PlayerEnum.teamAgeGroup]] } };
		}

		if (initialValue[PlayerEnum.teamLevel] && initialValue[PlayerEnum.teamLevel] !== "-") {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.teamLevel]: [initialValue[PlayerEnum.teamLevel]] } };
		}

		if (initialValue[PlayerEnum.playerAgeCategory] && initialValue[PlayerEnum.playerAgeCategory] !== "-") {
			leftFilter = { ...leftFilter, ...{ [PlayerEnum.playerAgeCategory]: [initialValue[PlayerEnum.playerAgeCategory]] } };
		}

		// Include club/team from storage (option objects with .value) or from defaults so they are sent in the initial request
		const storedClubId = (initialValue as any).clubId?.value ?? defaults?.clubId;
		const storedTeamId = (initialValue as any).teamId?.value ?? defaults?.teamId;
		return {
			...leftFilter,
			...(storedClubId != null ? { clubId: [storedClubId] } : {}),
			...(storedTeamId != null ? { teamId: [storedTeamId] } : {}),
		};
	}
	return {
		...(defaults?.clubId ? { clubId: [defaults.clubId] } : {}),
		...(defaults?.teamId ? { teamId: [defaults.teamId] } : {}),
	};
};
